import React, {useEffect, useRef} from 'react';
import {Animated, Pressable, StyleSheet, View} from 'react-native';

import {AppText} from '@/shared/ui/text';
import {radii, spacing, useTheme} from '@/shared/ui/theme';

/**
 * A labelled on/off switch — `Switch` ui-kit: тумблер с прямоугольным ходом и
 * прямоугольным сухарём. Скруглённый «бегунок» здесь смотрелся бы деталью из другого
 * прибора. Включённый — залит акцентом с белым сухарём; выключенный — `surface-base`
 * с рамкой `border-strong` и серым сухарём.
 *
 * Нарисован крупнее, чем у рабочей станции (44×24 против 38×20): the whole ROW is the
 * tap target so a thumb never has to find the track, but the track is also the only
 * thing on the row that shows state, and at 20pt tall it is hard to read the state of
 * at arm's length. An optional hint explains what the setting does.
 *
 * The knob SLIDES rather than jumping between ends. That 140ms is not decoration: it
 * is what tells the user their tap landed on the switch and not on the row behind it.
 */
const TRACK_W = 44;
const TRACK_H = 24;
const KNOB = 16;
const PAD = 3;
const TRAVEL = TRACK_W - KNOB - PAD * 2 - 2;

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing(3),
    paddingVertical: spacing(1),
  },
  text: {flex: 1, gap: 2},
  track: {
    width: TRACK_W,
    height: TRACK_H,
    borderRadius: radii.sm,
    borderWidth: 1,
    padding: PAD,
    justifyContent: 'center',
  },
  knob: {width: KNOB, height: KNOB, borderRadius: 1},
  /* Без подписи: коробка по размеру трека, чтобы ячейка таблицы не растягивалась. */
  bare: {alignSelf: 'flex-start'},
});

export function Toggle({
  label,
  value,
  onChange,
  hint,
  disabled = false,
  /**
   * Только переключатель, без подписи рядом.
   *
   * Для ячейки таблицы, где подпись уже стоит в заголовке столбца. Диктору подпись
   * по-прежнему нужна, поэтому она уезжает в `accessibilityLabel`, а не исчезает.
   */
  bare = false,
}: {
  label: string;
  value: boolean;
  onChange: (value: boolean) => void;
  hint?: string;
  disabled?: boolean;
  bare?: boolean;
}) {
  const p = useTheme();
  const slide = useRef(new Animated.Value(value ? 1 : 0)).current;

  useEffect(() => {
    Animated.timing(slide, {
      toValue: value ? 1 : 0,
      duration: 140,
      useNativeDriver: true,
    }).start();
  }, [value, slide]);

  return (
    <Pressable
      onPress={disabled ? undefined : () => onChange(!value)}
      disabled={disabled}
      accessibilityRole="switch"
      accessibilityState={{checked: value, disabled}}
      accessibilityLabel={label}
      style={[bare ? styles.bare : styles.row, disabled && {opacity: 0.5}]}>
      {bare ? null : (
        <View style={styles.text}>
          <AppText variant="body">{label}</AppText>
          {hint ? <AppText variant="caption">{hint}</AppText> : null}
        </View>
      )}
      <View
        style={[
          styles.track,
          {
            backgroundColor: value ? p.primary : p.background,
            borderColor: value ? p.primary : p.borderStrong,
          },
        ]}>
        <Animated.View
          style={[
            styles.knob,
            {
              backgroundColor: value ? p.primaryForeground : p.mutedForeground,
              transform: [
                {
                  translateX: slide.interpolate({
                    inputRange: [0, 1],
                    outputRange: [0, TRAVEL],
                  }),
                },
              ],
            },
          ]}
        />
      </View>
    </Pressable>
  );
}
